import React, { ReactElement, Fragment, useEffect, useState, useMemo } from "react";
import { gql, useQuery } from "urql";
import { Formik } from "formik";
import { cloneDeep } from "lodash";

import { OpenFrame, OpenTimeSeries, ValueType } from "@captor/openseries-ts";
import { DateHelper } from "../../../common/src";
import { SearchMultipleSelectField, ISelectOption } from "../../../components/src/form";
import { useQueryStateLazy, useQueryStateArray } from "../../../components/src/use-query-state";
import { PeriodSelector } from "../components/PeriodSelector";
import { DateForm } from "../../../components/src/dateForm";
import { TimeSeriesOperationPlot } from "./timeseries/TimeSeriesOperationPlot";
import { CompareMetricsTable } from "./CompareMetricsTable";
import { CompareAnnualBarChart } from "./CompareAnnualBarChart";
import { CurrencyEnum, InstrumentModelTypeEnum } from "../types.generated";

/** Captor colorway – matches vite-app, used for pills and charts */
const COMPARE_COLORWAY = ["#66725B", "#D0C0B1", "#253551", "#8D929D", "#611A51", "#402D16", "#5D6C85", "#404752"];

interface DataProp {
    _id: string;
    instrumentId: string;
    type: string;
    instrument: {
        name: string;
        longName: string;
        currency: CurrencyEnum;
        modelType: InstrumentModelTypeEnum;
        bloombergTicker: string;
        isin: string;
    };
}

interface CompareInstrument {
    _id: string;
    name: string;
    longName: string;
    currency: CurrencyEnum;
    modelType: InstrumentModelTypeEnum;
    type: string;
    exchangedCurrency: CurrencyEnum;
    fxHedged: boolean;
    index: number;
}

const GET_DATA = gql`
    query {
        timeseries(filter: { statusIn: Active, typeIn: ["Price(Close)", "Return(Total)"] }) {
            _id
            instrumentId
            type
            instrument {
                modelType
                name
                longName
                currency
                bloombergTicker
                isin
            }
        }
    }
`;

const DERIVATIVE_MODEL_TYPES = {
    [InstrumentModelTypeEnum.Swap]: true,
    [InstrumentModelTypeEnum.Swaption]: true,
    [InstrumentModelTypeEnum.FxSwap]: true
};

const TIMESERIES_TYPE_OPTIONS = [
    { value: "Price(Close)", label: "Price(Close)" },
    { value: "Return(Total)", label: "Return(Total)" }
];

const Checkbox = (props) => <input type="checkbox" {...props} />;

const defaultStartDate = () => DateHelper.addYears(new Date(), -1).toISOString().slice(0, 10);
const defaultEndDate = () => new Date().toISOString().slice(0, 10);

export function ComparePage(): ReactElement {
    const [rebase, setRebase] = useQueryStateLazy("rebase", "true");
    const [alignDateRange, setAlignDateRange] = useQueryStateLazy("align", "true");
    const [timeseriesType, setTimeseriesType] = useQueryStateLazy("type", "Price(Close)");

    const [startDate] = useQueryStateLazy("startDate", defaultStartDate());
    const [endDate] = useQueryStateLazy("endDate", defaultEndDate());

    const [timeserieInstrumentIds, setTimeserieInstrumentIds] = useQueryStateArray("ts", []);
    const [exchange, setExchange] = useQueryStateArray("ex", []);
    const [fxHedged, setFxHedged] = useQueryStateArray("fx", []);

    const [instruments, setInstruments] = useState([] as CompareInstrument[]);

    const addTypeInName = false;
    const useRangeSelector = false;
    const margin = { t: 0, b: 0, l: 8, r: 8, pad: 0 };

    const [{ data, fetching: loading, error }] = useQuery({
        query: GET_DATA,
        requestPolicy: "cache-and-network"
    });

    const typeIn = timeseriesType === "Return(Total)" ? `["DerivedTotalReturn"]` : `["Price(Close)"]`;
    const timeseriesQuery = useMemo(() => {
        if (instruments.length === 0) return null;
        const start = startDate ? ` startDate: "${startDate}",` : "";
        return gql`
            query timeseriesCompare {
                ${instruments
                    .map(
                        (item, idx: number) => `
                    ts${idx}: timeserie(_id: "${item._id}") {
                        instrument {
                            _id
                            name
                            longName
                            exchanged: timeSeries(typeIn: ${typeIn}, exchanged: ${item.exchangedCurrency}, fxHedged: ${item.fxHedged},${start} endDate: "${endDate}") {
                                type
                                values
                                dates
                            }
                        }
                    }
                `
                    )
                    .join("")}
            }
        `;
    }, [instruments, startDate, endDate, typeIn]);

    const [{ data: timeseriesData, fetching: timeseriesFetching }] = useQuery({
        query: timeseriesQuery ?? gql`query empty { __typename }`,
        requestPolicy: "cache-and-network",
        pause: instruments.length === 0 || !timeseriesQuery
    });

    const alignedTimeseries = useMemo(() => {
        if (!timeseriesData || instruments.length === 0) return [];
        const list: OpenTimeSeries[] = [];
        for (let index = 0; index < instruments.length; index++) {
            const value = instruments[index];
            const t = timeseriesData[`ts${index}`];
            if (!t?.instrument?.exchanged?.[0]) continue;
            const raw = t.instrument.exchanged[0];
            const datesRaw = raw?.dates ?? raw?.timestamps ?? [];
            if (!datesRaw.length || !Array.isArray(raw?.values)) continue;
            const dates: string[] = datesRaw.map((d: string) => (typeof d === "string" ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10)));
            const values: number[] = (raw.values ?? []).map((v: number) => parseFloat(String(v)));
            if (dates.length === 0 || values.length === 0) continue;
            const name: string = t.instrument.longName ?? t.instrument.name ?? `Series ${index}`;
            const fxHedgedLbl = value.fxHedged ? " FX Hedged" : "";
            const label = `${name} (${value.exchangedCurrency}${fxHedgedLbl})`;
            const valuetype = timeseriesType === "Return(Total)" ? ValueType.RTRN : ValueType.PRICE;
            let ots = OpenTimeSeries.fromArrays(label, dates, values, {
                valuetype,
                timeseriesId: value._id,
                instrumentId: t.instrument._id,
                baseccy: value.exchangedCurrency
            });
            if (rebase === "true" && values[0] !== 0) {
                const normValues = values.map((v) => v / values[0]);
                ots = OpenTimeSeries.fromArrays(label, dates, normValues, {
                    valuetype,
                    timeseriesId: value._id,
                    instrumentId: t.instrument._id,
                    baseccy: value.exchangedCurrency
                });
            }
            list.push(ots);
        }
        if (alignDateRange === "true" && list.length > 1) {
            const frame = new OpenFrame(list).mergeSeries("inner");
            if (frame.tsdf.dates.length > 0) {
                return frame.columnLabels.map((label, i) =>
                    OpenTimeSeries.fromArrays(label, frame.tsdf.dates, frame.tsdf.columns[i], {
                        valuetype: list[i].valuetype,
                        timeseriesId: list[i].timeseriesId,
                        instrumentId: list[i].instrumentId,
                        baseccy: list[i].currency
                    })
                );
            }
        }
        return list;
    }, [timeseriesData, instruments, rebase, alignDateRange, timeseriesType]);

    useEffect(() => {
        const ids = instruments && instruments.length > 0 ? instruments.map((i) => i._id) : [];
        const requestedIds = Array.isArray(timeserieInstrumentIds) ? timeserieInstrumentIds : [];
        if (data && data.timeseries && ids.toString() !== requestedIds.toString()) {
            const updatedInstruments: CompareInstrument[] = [];
            for (let index = 0; index < requestedIds.length; index++) {
                const instrumentId = requestedIds[index];
                if (!instrumentId || typeof instrumentId !== "string") continue;
                const existingInstrument = instruments.find((x) => x._id == instrumentId);
                if (existingInstrument) {
                    const instr = cloneDeep(existingInstrument);
                    instr.index = index;
                    updatedInstruments.push(instr);
                } else {
                    const ts = data.timeseries.find((t: DataProp) => t._id == instrumentId);
                    if (!ts) continue;
                    let parsedFx = false;
                    if ((fxHedged ?? []).length > index) {
                        try {
                            parsedFx = JSON.parse(String(fxHedged[index])) === true;
                        } catch {
                            parsedFx = false;
                        }
                    }
                    updatedInstruments.push({
                        _id: ts._id,
                        name: ts.instrument.name,
                        longName: ts.instrument.longName,
                        currency: ts.instrument.currency,
                        modelType: ts.instrument.modelType,
                        type: ts.type,
                        exchangedCurrency:
                            (exchange ?? []).length > index
                                ? (exchange[index] as CurrencyEnum)
                                : ts.instrument.currency,
                        fxHedged: parsedFx,
                        index
                    });
                }
            }
            setInstruments(updatedInstruments);
            setExchange(updatedInstruments.map((x) => x.exchangedCurrency));
            setFxHedged(updatedInstruments.map((x) => x.fxHedged.toString()));
            if (updatedInstruments.length !== requestedIds.length) {
                setTimeserieInstrumentIds(updatedInstruments.map((x) => x._id));
            }
        }
    }, [instruments, timeserieInstrumentIds, data, exchange, setExchange, fxHedged, setFxHedged, setTimeserieInstrumentIds]);

    const options = useMemo(() => {
        let options = [];
        if (data && data.timeseries) {
            if (timeseriesType === "Return(Total)") {
                const localOptions = [];
                for (const t of data.timeseries as DataProp[]) {
                    if (
                        t.type === "Return(Total)" &&
                        t.instrument &&
                        t.instrument.modelType &&
                        !DERIVATIVE_MODEL_TYPES[t.instrument.modelType]
                    ) {
                        localOptions.push({
                            _id: t._id,
                            name: t.instrument.name,
                            longName: t.instrument.longName,
                            isin: t.instrument.isin,
                            currency: t.instrument.currency,
                            bloombergTicker: t.instrument.bloombergTicker
                        });
                    }
                }
                options = localOptions;
            } else {
                const localOptions = [];
                for (const t of data.timeseries as DataProp[]) {
                    if (t.type === "Price(Close)") {
                        localOptions.push({
                            _id: t._id,
                            name: t.instrument.name,
                            longName: t.instrument.longName,
                            isin: t.instrument.isin,
                            currency: t.instrument.currency,
                            bloombergTicker: t.instrument.bloombergTicker
                        });
                    }
                }
                options = localOptions;
            }
        }
        return options;
    }, [data, timeseriesType]);

    const selectOptions: ISelectOption[] = useMemo(
        () =>
            options.map((o: { _id: string; longName?: string; name: string }) => ({
                key: o._id,
                value: o.longName || o.name,
                text: o.longName || o.name
            })),
        [options]
    );

    if (loading)
        return (
            <div>
                <p>Loading...</p>
            </div>
        );

    if (error)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    const handleRebase = (e) => {
        if (e.target.checked) {
            setRebase("true");
        } else {
            setRebase("false");
        }
    };

    const handleAlignDateRange = (e) => {
        if (e.target.checked) {
            setAlignDateRange("true");
        } else {
            setAlignDateRange("false");
        }
    };

    const handleTimeseriesTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTimeseriesType(e.target.value as "Price(Close)" | "Return(Total)");
        setTimeserieInstrumentIds([]);
    };

    const uniqueCurrencies: string[] = [...new Set<string>(options.map((item: any) => item.currency))];

    const handleClear = () => {
        setTimeserieInstrumentIds([]);
    };

    const getPillStyle = (key: string, index: number) => {
        const c = COMPARE_COLORWAY[index % COMPARE_COLORWAY.length];
        return { backgroundColor: c, color: "#fff", borderColor: c };
    };

    return (
        <Fragment>
            {/* Top section: all controls - chooser with pills inside the field */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div className="flex-1 min-w-0" style={{ minWidth: 280 }}>
                    <Formik
                        key={`${timeseriesType}-${(timeserieInstrumentIds ?? []).join(",")}`}
                        enableReinitialize={true}
                        initialValues={{ selectedIds: Array.isArray(timeserieInstrumentIds) ? timeserieInstrumentIds : [] }}
                        onSubmit={() => {}}
                    >
                        {() => (
                            <SearchMultipleSelectField
                                name="selectedIds"
                                label="Choose timeseries"
                                className="mb-0"
                                options={selectOptions}
                                disabled={false}
                                placeholder="Choose timeseries..."
                                onChange={(keys) => setTimeserieInstrumentIds(Array.isArray(keys) ? keys : [])}
                                getPillStyle={getPillStyle}
                                clearable
                            />
                        )}
                    </Formik>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <PeriodSelector firstAvailableStartDate="" endDate={endDate} lazyUrl />
                    <DateForm className="width-fixed" defaultDateString={startDate} dateName="startDate" label="From date" lazyUrl />
                    <DateForm className="width-fixed" defaultDateString={endDate} dateName="endDate" label="To date" lazyUrl />
                    <div>
                        <Checkbox
                            checked={rebase === "true"}
                            onChange={handleRebase}
                            disabled={(timeserieInstrumentIds ?? []).length === 0}
                        />{" "}
                        <span className="text-sm">Rebase to start</span>
                    </div>
                    <div>
                        <Checkbox
                            checked={alignDateRange === "true"}
                            onChange={handleAlignDateRange}
                            disabled={(timeserieInstrumentIds ?? []).length === 0}
                        />{" "}
                        <span className="text-sm">Align periods</span>
                    </div>
                    {instruments.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-gray-600 self-center">Per series:</span>
                            {instruments.map((inst, idx) => (
                                <div key={inst._id} className="flex items-center gap-1 border-l pl-2 first:border-0 first:pl-0">
                                    <span className="text-xs truncate max-w-[100px]" title={inst.name}>
                                        {inst.name}
                                    </span>
                                    <select
                                        data-ndx={idx}
                                        className="text-sm py-0.5"
                                        value={inst.exchangedCurrency}
                                        onChange={(e) => {
                                            const updated = cloneDeep(instruments);
                                            updated[idx].exchangedCurrency = e.target.value as CurrencyEnum;
                                            setInstruments(updated);
                                            setExchange(updated.map((x) => x.exchangedCurrency));
                                        }}
                                    >
                                        {uniqueCurrencies.map((d: string, i) => (
                                            <option key={i} value={d}>
                                                {d}
                                            </option>
                                        ))}
                                    </select>
                                    <label className="flex items-center gap-0.5 text-xs">
                                        <input
                                            type="checkbox"
                                            data-ndx={idx}
                                            checked={inst.fxHedged}
                                            onChange={(e) => {
                                                const updated = cloneDeep(instruments);
                                                updated[idx].fxHedged = e.target.checked;
                                                setInstruments(updated);
                                                setFxHedged(updated.map((x) => x.fxHedged.toString()));
                                            }}
                                        />
                                        FX
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                    <div>
                        <label htmlFor="timeseriesType" className="mr-1 text-sm">
                            Type:
                        </label>
                        <select
                            id="timeseriesType"
                            className="text-sm"
                            value={timeseriesType}
                            onChange={handleTimeseriesTypeChange}
                        >
                            {TIMESERIES_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button type="button" onClick={handleClear} className="text-sky-600 hover:underline text-sm px-2 py-1">
                        Clear
                    </button>
                </div>
            </div>

            {/* Main content: 2-column layout - separate line + bar charts left, metrics table right */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                <div className="lg:col-span-2 flex flex-col gap-4 min-h-[560px]">
                    {instruments.length > 0 ? (
                        timeseriesFetching ? (
                            <p>Loading time series...</p>
                        ) : alignedTimeseries.length > 0 ? (
                            <Fragment>
                                <div className="bg-white rounded border border-gray-200 overflow-hidden flex-[3] min-h-[280px] relative">
                                    <TimeSeriesOperationPlot
                                        params={instruments}
                                        startDate={startDate}
                                        endDate={endDate}
                                        alignDateRange={alignDateRange === "true"}
                                        rebase={rebase === "true"}
                                        addTypeInName={addTypeInName}
                                        useRangeSelector={useRangeSelector}
                                        customMargin={margin}
                                        totalReturn={timeseriesType === "Return(Total)"}
                                        preloadedData={timeseriesData as Record<string, unknown>}
                                        preloadedOpenTimeseries={alignedTimeseries}
                                        colors={instruments.map((_, i) => COMPARE_COLORWAY[i % COMPARE_COLORWAY.length])}
                                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                                    />
                                </div>
                                <div className="bg-white rounded border border-gray-200 overflow-hidden flex-[2] min-h-[260px] relative">
                                    <CompareAnnualBarChart
                                        timeseries={alignedTimeseries}
                                        colors={instruments.map((_, i) => COMPARE_COLORWAY[i % COMPARE_COLORWAY.length])}
                                        startDate={startDate}
                                        endDate={endDate}
                                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                                    />
                                </div>
                            </Fragment>
                        ) : (
                            <p>Loading...</p>
                        )
                    ) : (
                        <p className="text-gray-500">Select timeseries to compare</p>
                    )}
                </div>
                <div className="lg:col-span-1 min-h-[560px] flex flex-col">
                    {alignedTimeseries.length > 0 ? (
                        <div className="bg-white rounded border border-gray-200 p-4 flex flex-col flex-1 overflow-auto">
                            <div className="flex-1 min-h-0 overflow-auto">
                                <CompareMetricsTable timeseries={alignedTimeseries} />
                            </div>
                        </div>
                    ) : (
                        <div className="text-gray-500 text-sm">Metrics appear when timeseries are selected</div>
                    )}
                </div>
            </div>
        </Fragment>
    );
}
